import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bar } from 'react-chartjs-2';
import type { ChartOptions, TooltipItem } from 'chart.js';

import {
  fetchRevenueSummary,
  fetchRevenueTimeline,
  type DateRangePreset,
} from '../../services/analytics';
import { ensureChartsRegistered } from './chartSetup';

ensureChartsRegistered();

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value ?? 0);

const PERIOD_OPTIONS: { value: DateRangePreset; label: string }[] = [
  { value: 'today', label: 'Bugün' },
  { value: 'yesterday', label: 'Dün' },
  { value: 'this_week', label: 'Bu Hafta' },
  { value: 'this_month', label: 'Bu Ay' },
  { value: 'this_year', label: 'Bu Yıl' },
  { value: 'last_30_days', label: 'Son 30 Gün' },
];

interface ExecutiveOverviewProps {
  restaurantId?: string;
  restaurantLabel?: string | null;
}

export default function ExecutiveOverview({
  restaurantId,
  restaurantLabel,
}: ExecutiveOverviewProps = {}) {
  const [period, setPeriod] = useState<DateRangePreset>('this_month');

  const { data, error, isError, isLoading, isFetching } = useQuery({
    queryKey: ['analytics', 'revenue-summary', period, restaurantId ?? 'all'],
    queryFn: () => fetchRevenueSummary(period, restaurantId),
  });

  const { data: timeline = [] } = useQuery({
    queryKey: ['analytics', 'revenue-timeline', period, restaurantId ?? 'all'],
    queryFn: () => fetchRevenueTimeline(period, restaurantId),
  });

  const periodLabel = PERIOD_OPTIONS.find((p) => p.value === period)?.label ?? '';
  const scopeLabel = restaurantLabel ?? 'Tüm şubeler';

  /* ── Chart ── */
  const chartData = useMemo(() => {
    const labels = timeline.map((p) => p.label);
    const revenues = timeline.map((p) => p.revenue);
    const maxRev = Math.max(...revenues, 0);
    const bgColors = revenues.map((r) =>
      r === maxRev && maxRev > 0 ? '#2563eb' : 'rgba(37, 99, 235, 0.35)'
    );
    return {
      labels,
      datasets: [{ data: revenues, backgroundColor: bgColors, borderRadius: 3, borderSkipped: false as const }],
    };
  }, [timeline]);

  const chartOptions: ChartOptions<'bar'> = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          title: (items) => items[0]?.label ?? '',
          label: (ctx: TooltipItem<'bar'>) => {
            const idx = ctx.dataIndex;
            const rev = ctx.parsed.y ?? 0;
            const orders = timeline[idx]?.orderCount ?? 0;
            return [`₺${rev.toFixed(2)}`, `${orders} sipariş`];
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 10 }, maxRotation: 0 },
      },
      y: {
        beginAtZero: true,
        ticks: {
          font: { size: 10 },
          callback: (v) => `₺${typeof v === 'number' ? v.toFixed(0) : '0'}`,
        },
        grid: { color: 'rgba(0,0,0,0.04)' },
      },
    },
  }), [timeline]);

  /* ── Error state ── */
  if (isError) {
    return (
      <div className="card">
        <div className="card-header">
          <div><p className="card-title">Gelir Özeti</p></div>
        </div>
        <div className="empty-state">
          <strong>Veriler yüklenemedi</strong>
          <p>{error instanceof Error ? error.message : 'Orders tablosu için Supabase izinlerini kontrol edin.'}</p>
        </div>
      </div>
    );
  }

  /* ── Payment percentages ── */
  const totalPaid =
    (data?.paymentBreakdown.cash.revenue ?? 0) +
    (data?.paymentBreakdown.credit_card.revenue ?? 0) +
    (data?.paymentBreakdown.online_card.revenue ?? 0) +
    (data?.paymentBreakdown.meal_card.revenue ?? 0) +
    (data?.paymentBreakdown.yemeksepeti.revenue ?? 0);

  function pct(val: number) {
    if (!totalPaid) return '0';
    return ((val / totalPaid) * 100).toFixed(1);
  }

  return (
    <div className="card">
      {/* Header */}
      <div className="card-header">
        <div>
          <p className="card-title">Gelir Özeti</p>
          <p className="card-subtitle">
            {isLoading || isFetching ? 'Hesaplanıyor...' : `${periodLabel} performans özeti · ${scopeLabel}`}
          </p>
        </div>
        <div className="period-selector">
          {PERIOD_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              className={`period-btn${period === opt.value ? ' active' : ''}`}
              onClick={() => setPeriod(opt.value)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Inline chart */}
      <div className="overview-chart-wrap">
        <Bar data={chartData} options={chartOptions} />
      </div>

      {/* KPI cards */}
      <div className="insight-grid">
        <div className="insight-card highlight">
          <span>Toplam ciro</span>
          <strong>{formatCurrency(data?.totalRevenue ?? 0)}</strong>
        </div>
        <div className="insight-card">
          <span>Tamamlanan sipariş</span>
          <strong>{data?.totalOrders ?? 0}</strong>
        </div>
        <div className="insight-card">
          <span>Ortalama sepet tutarı</span>
          <strong>{formatCurrency(data?.averageOrderValue ?? 0)}</strong>
        </div>
        <div className="insight-card">
          <span>Aktif müşteri</span>
          <strong>{data?.activeCustomers ?? 0}</strong>
        </div>
        <div className="insight-card">
          <span>Bekleyen sipariş</span>
          <strong className={data && data.pendingOrders > 0 ? 'trend-down' : ''}>
            {data?.pendingOrders ?? 0}
          </strong>
        </div>
        <div className="insight-card">
          <span>İptal edilen</span>
          <strong className={data && data.cancelledOrders > 0 ? 'trend-down' : ''}>
            {data?.cancelledOrders ?? 0}
          </strong>
        </div>
        <div className="insight-card">
          <span>İptal tutarı</span>
          <strong className={data && data.cancelledAmount > 0 ? 'trend-down' : ''}>
            {formatCurrency(data?.cancelledAmount ?? 0)}
          </strong>
        </div>
      </div>

      {/* Payment breakdown */}
      <div className="payment-breakdown">
        <p className="breakdown-title">Ödeme Yöntemi Dağılımı</p>
        <div className="breakdown-grid">
          <div className="breakdown-card">
            <div className="breakdown-bar cash-bar" />
            <div className="breakdown-info">
              <span className="breakdown-label">Nakit</span>
              <strong>{formatCurrency(data?.paymentBreakdown.cash.revenue ?? 0)}</strong>
              <div className="breakdown-meta">
                <span>{data?.paymentBreakdown.cash.orders ?? 0} sipariş</span>
                <span className="breakdown-pct">%{pct(data?.paymentBreakdown.cash.revenue ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className="breakdown-card">
            <div className="breakdown-bar card-bar" />
            <div className="breakdown-info">
              <span className="breakdown-label">Kredi Kartı</span>
              <strong>{formatCurrency(data?.paymentBreakdown.credit_card.revenue ?? 0)}</strong>
              <div className="breakdown-meta">
                <span>{data?.paymentBreakdown.credit_card.orders ?? 0} sipariş</span>
                <span className="breakdown-pct">%{pct(data?.paymentBreakdown.credit_card.revenue ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className="breakdown-card">
            <div className="breakdown-bar online-bar" />
            <div className="breakdown-info">
              <span className="breakdown-label">Online Ödeme</span>
              <strong>{formatCurrency(data?.paymentBreakdown.online_card.revenue ?? 0)}</strong>
              <div className="breakdown-meta">
                <span>{data?.paymentBreakdown.online_card.orders ?? 0} sipariş</span>
                <span className="breakdown-pct">%{pct(data?.paymentBreakdown.online_card.revenue ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className="breakdown-card">
            <div className="breakdown-bar meal-bar" />
            <div className="breakdown-info">
              <span className="breakdown-label">Yemek Kartı</span>
              <strong>{formatCurrency(data?.paymentBreakdown.meal_card.revenue ?? 0)}</strong>
              <div className="breakdown-meta">
                <span>{data?.paymentBreakdown.meal_card.orders ?? 0} sipariş</span>
                <span className="breakdown-pct">%{pct(data?.paymentBreakdown.meal_card.revenue ?? 0)}</span>
              </div>
            </div>
          </div>
          <div className="breakdown-card">
            <div className="breakdown-bar vale-bar" />
            <div className="breakdown-info">
              <span className="breakdown-label">YS Vale</span>
              <strong>{formatCurrency(data?.paymentBreakdown.yemeksepeti.revenue ?? 0)}</strong>
              <div className="breakdown-meta">
                <span>{data?.paymentBreakdown.yemeksepeti.orders ?? 0} sipariş</span>
                <span className="breakdown-pct">%{pct(data?.paymentBreakdown.yemeksepeti.revenue ?? 0)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
